import type { AppendixSummary, CodeItemGroup, CodeRow } from '../types/codeIndex'

// Appendix references are lists of appendix domain ids kept on a code item, on a value, or on the
// code item keyed by value-group name. They are saved with the sheet and resolved to letters through
// the codesheet's appendix summaries (`AppendixSummary.key`).

export type AppendixRefSource = 'own' | 'codeItem' | 'group'

export type ResolvedAppendixRef = {
  key: string
  letter: string
  title: string
  source: AppendixRefSource
  sourceLabel: string
}

export type AppendixRefTargetKind = 'codeItem' | 'value' | 'group'

export type AppendixSummariesByKey = ReadonlyMap<string, AppendixSummary>

export function normalizeAppendixRefs(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  const seen = new Set<string>()
  value.forEach(entry => {
    const key = typeof entry === 'string' ? entry.trim() : ''
    if (key) seen.add(key)
  })
  return Array.from(seen).sort((a, b) => (a < b ? -1 : a > b ? 1 : 0))
}

export function normalizeGroupAppendixRefs(value: unknown): Record<string, string[]> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {}
  const result: Record<string, string[]> = {}
  Object.entries(value as Record<string, unknown>).forEach(([name, refs]) => {
    const groupName = typeof name === 'string' ? name.trim() : ''
    const normalized = normalizeAppendixRefs(refs)
    if (groupName && normalized.length > 0) result[groupName] = normalized
  })
  return result
}

export function addAppendixRef(refs: string[] | null | undefined, key: string): string[] {
  return normalizeAppendixRefs([...(refs ?? []), key])
}

export function removeAppendixRef(refs: string[] | null | undefined, key: string): string[] {
  return normalizeAppendixRefs((refs ?? []).filter(ref => ref !== key))
}

export function rekeyGroupAppendixRefs(
  map: Record<string, string[]> | null | undefined,
  oldName: string | null | undefined,
  newName: string | null | undefined
): Record<string, string[]> {
  const normalized = normalizeGroupAppendixRefs(map)
  const from = (oldName ?? '').trim()
  const to = (newName ?? '').trim()
  if (!from || from === to || !normalized[from]) return normalized

  const { [from]: moved, ...rest } = normalized
  if (!to) return rest
  return normalizeGroupAppendixRefs({ ...rest, [to]: [...(rest[to] ?? []), ...moved] })
}

// Drops group entries that no value uses any more (rows moved to another group or deleted).
export function pruneGroupAppendixRefs(
  map: Record<string, string[]> | null | undefined,
  rows: ReadonlyArray<Pick<CodeRow, 'group'>>
): Record<string, string[]> {
  const normalized = normalizeGroupAppendixRefs(map)
  const used = new Set(rows.map(row => (row.group ?? '').trim()).filter(Boolean))
  return Object.fromEntries(Object.entries(normalized).filter(([name]) => used.has(name)))
}

export function buildAppendixSummariesByKey(summaries: ReadonlyArray<AppendixSummary>): Map<string, AppendixSummary> {
  const map = new Map<string, AppendixSummary>()
  summaries.forEach(summary => {
    if (summary.key) map.set(summary.key, summary)
  })
  return map
}

// "C02" -> "02": the code item number shown to users.
export function formatCodeItemNumber(code: string | null | undefined): string {
  const raw = (code ?? '').trim()
  return raw.replace(/^C(?=\d)/i, '') || raw
}

function compareByLetter(a: ResolvedAppendixRef, b: ResolvedAppendixRef): number {
  if (a.letter.length !== b.letter.length) return a.letter.length - b.letter.length
  return a.letter < b.letter ? -1 : a.letter > b.letter ? 1 : 0
}

function resolveList(
  keys: string[] | null | undefined,
  source: AppendixRefSource,
  sourceLabel: string,
  summariesByKey: AppendixSummariesByKey
): ResolvedAppendixRef[] {
  return normalizeAppendixRefs(keys)
    .map(key => {
      const summary = summariesByKey.get(key)
      if (!summary) return null
      return { key, letter: summary.letter || '?', title: summary.title || 'Untitled appendix', source, sourceLabel }
    })
    .filter((ref): ref is ResolvedAppendixRef => Boolean(ref))
    .sort(compareByLetter)
}

export function codeItemSourceLabel(group: Pick<CodeItemGroup, 'codeItemPos'>): string {
  return `inherited from code item ${formatCodeItemNumber(group.codeItemPos.code)}`
}

export function groupSourceLabel(sectionName: string): string {
  return `inherited from group ${sectionName}`
}

/**
 * Every appendix that applies to a target, with where it comes from. For a code item only its own
 * refs; for a group its own refs plus the code item's; for a value its own refs plus the group's and
 * the code item's. Own refs come first, then inherited ones; each block is sorted by letter.
 */
export function resolveAppendixRefs(
  group: CodeItemGroup,
  row: CodeRow | null | undefined,
  sectionName: string | null | undefined,
  summariesByKey: AppendixSummariesByKey
): ResolvedAppendixRef[] {
  const groupName = (sectionName ?? '').trim()
  const groupRefs = groupName ? normalizeGroupAppendixRefs(group.groupAppendixRefs)[groupName] ?? [] : []
  const itemLabel = codeItemSourceLabel(group)

  if (row) {
    return [
      ...resolveList(row.appendixRefs, 'own', 'set on this value only', summariesByKey),
      ...resolveList(group.appendixRefs, 'codeItem', itemLabel, summariesByKey),
      ...(groupName ? resolveList(groupRefs, 'group', groupSourceLabel(groupName), summariesByKey) : []),
    ]
  }

  if (groupName) {
    return [
      ...resolveList(groupRefs, 'own', `set on group ${groupName}`, summariesByKey),
      ...resolveList(group.appendixRefs, 'codeItem', itemLabel, summariesByKey),
    ]
  }

  return resolveList(group.appendixRefs, 'own', 'set on this code item', summariesByKey)
}

export function formatAppendixRefTooltip(refs: ReadonlyArray<ResolvedAppendixRef>): string {
  return refs.map(ref => `${ref.letter} — ${ref.sourceLabel}`).join('\n')
}

/** Subtitle of the reference modals, e.g. "Code item 02 · TYPE — applies to all 5 values". */
export function describeAppendixRefTarget(
  kind: AppendixRefTargetKind,
  group: CodeItemGroup,
  row?: CodeRow | null,
  sectionName?: string | null
): string {
  const number = formatCodeItemNumber(group.codeItemPos.code)

  if (kind === 'value' && row) {
    const code = row.optionCode || '—'
    const description = (row.description ?? '').trim()
    return `Value ${code}${description ? ` · ${description}` : ''} — applies to this value only`
  }

  if (kind === 'group') {
    const name = (sectionName ?? '').trim()
    const count = group.rows.filter(candidate => (candidate.group ?? '').trim() === name).length
    return `Group ${name} · code item ${number} — applies to ${formatValueCount(count)} in this group`
  }

  const title = (group.title ?? '').trim()
  return `Code item ${number}${title ? ` · ${title}` : ''} — applies to ${formatValueCount(group.rows.length)}`
}

function formatValueCount(count: number): string {
  if (count === 1) return 'its only value'
  return `all ${count} values`
}
